import React, { useState, useEffect } from 'react';
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore';
import { SparkResultKind } from '../../types/sparkTypes';
import styles from '../../styles/SparkActionModal.module.css';
import { getResultHandoffText } from '../../utils/resultHandoff';
import CloseIcon from '@mui/icons-material/Close';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import ReactDOM from 'react-dom';

interface SparkActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  actionName: string;
  projectFqn?: string | null;
  libraryItemFqn?: string | null;
  actionArgs?: Record<string, any>;
  onSuccess?: (jobFqn: string) => void;
  isGuidanceRequired?: boolean;
  showAutoEnhanceToggle?: boolean;
  autoEnhanceLabel?: string;
  autoEnhanceTooltip?: string;
  thumbnailUrl?: string;
  thumbnailAlt?: string;
  guidancePlaceholder?: string;
  sourceTypeFqn?: string | null;
  targetTypeFqn?: string | null;
  resultKind?: SparkResultKind | null;
  outputTypeName?: string;
  currentTypeName?: string;
  isCurrentStep?: boolean;
}

export const SparkActionModal: React.FC<SparkActionModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  actionName,
  projectFqn,
  libraryItemFqn,
  actionArgs = {},
  onSuccess,
  isGuidanceRequired = false,
  showAutoEnhanceToggle = false,
  autoEnhanceLabel = "Auto-enhance prompt",
  autoEnhanceTooltip = "Automatically enhance prompt with business context",
  thumbnailUrl,
  thumbnailAlt,
  guidancePlaceholder,
  sourceTypeFqn,
  targetTypeFqn,
  resultKind = null,
  outputTypeName,
  currentTypeName,
  isCurrentStep = false,
}) => {
  const [guidance, setGuidance] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoEnhancePrompt, setAutoEnhancePrompt] = useState(true);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const triggerSparkAction = useAtlasStore((state: AtlasState) => state.triggerSparkAction);
  const isLoadingAction = useAtlasStore((state: AtlasState) => state.isLoadingSpark.action);
  const sparkError = useAtlasStore((state: AtlasState) => state.sparkError);
  const setSparkUserInteractionBlockingRefresh = useAtlasStore(
    (state: AtlasState) => state.setSparkUserInteractionBlockingRefresh
  );

  // Block background refresh while modal is open
  useEffect(() => {
    if (isOpen) {
      setSparkUserInteractionBlockingRefresh(true);
      return () => {
        setSparkUserInteractionBlockingRefresh(false);
      };
    }
  }, [isOpen, setSparkUserInteractionBlockingRefresh]);

  // Reset state when modal is opened
  useEffect(() => {
    if (isOpen) {
      setGuidance('');
      setIsSubmitting(false);
      setAutoEnhancePrompt(true);
    }
  }, [isOpen]);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      // Use setTimeout to ensure the modal is fully rendered before focusing
      const timeoutId = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    // If guidance is required but empty, don't proceed
    if (isGuidanceRequired && !guidance.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Merge provided actionArgs with guidance
      const fullActionArgs: Record<string, any> = {
        ...actionArgs,
        guidance: guidance.trim(),
      };

      // Determine the actual action name based on auto-enhance toggle
      let finalActionName = actionName;
      if (showAutoEnhanceToggle && !autoEnhancePrompt) {
        // If NOT auto-enhancing (raw prompt) and the action is generate_images, switch to generate_images_raw
        if (actionName === 'generate_images') {
          finalActionName = 'generate_images_raw';
        }
      }

      await triggerSparkAction(
        { projectFqn: projectFqn ?? null, libraryItemFqn: libraryItemFqn ?? null },
        finalActionName,
        fullActionArgs
      );

      // Call onSuccess without relying on result from triggerSparkAction
      if (onSuccess) {
        onSuccess(`${finalActionName}-${Date.now()}`); // Generate a temporary ID if needed
      }

      onClose();
    } catch (error) {
      console.error('Failed to trigger action:', error);
      // Error will be displayed via sparkError state
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!isGenerateDisabled) {
        handleSubmit();
      }
    }
  };

  // Check if the Generate button should be disabled
  const isGenerateDisabled = isSubmitting ||
    isLoadingAction ||
    (isGuidanceRequired && !guidance.trim());

  const resolvedGuidancePlaceholder = React.useMemo(() => {
    const basePlaceholder = (
      guidancePlaceholder
      ?? (isGuidanceRequired
        ? 'Tell DeepSights exactly what you want it to do'
        : 'Add any extra guidance, constraints, or preferences')
    ).trim();

    if (/^(optional|required)\s*:/i.test(basePlaceholder)) {
      return basePlaceholder;
    }

    return `${isGuidanceRequired ? 'Required' : 'Optional'}: ${basePlaceholder}`;
  }, [guidancePlaceholder, isGuidanceRequired]);

  const helperText = getResultHandoffText({
    resultKind,
    outputTypeName,
    currentTypeName,
    isCurrentStep,
    includeActivityHint: true,
  });

  if (!isOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} data-modal-root="true" onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerTitleGroup}>
            <h2 className={styles.modalTitle}>{title}</h2>
            <p className={styles.modalSubtitle}>{description}</p>
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
            disabled={isSubmitting || isLoadingAction}
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.modalBody}>
          {thumbnailUrl && (
            <div className={styles.thumbnailContainer}>
              <img
                src={thumbnailUrl}
                alt={thumbnailAlt || 'Selected image'}
                className={styles.thumbnail}
              />
            </div>
          )}
          {sparkError && (
            <div className={styles.errorMessage} role="alert">
              {sparkError}
            </div>
          )}

          <div className={styles.formGroup}>
            {helperText && (
              <p className={styles.launchHelperText}>{helperText}</p>
            )}
            {/* Info box when guidance is optional */}
            {!isGuidanceRequired && (
              <div className={styles.infoBox}>
                <InfoOutlinedIcon fontSize="small" />
                <span>
                  DeepSights will automatically have access to your briefing, resources, and all project context.
                  You may specify any <em>additional</em> guidance here, if desired.
                </span>
              </div>
            )}
            {/* Warning box when guidance is required */}
            {isGuidanceRequired && (
              <div className={styles.warningBox}>
                <WarningAmberIcon fontSize="small" />
                <span>DeepSights needs your specific guidance for this task.</span>
              </div>
            )}
            <textarea
              ref={textareaRef}
              id="guidance"
              className={styles.textarea}
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={resolvedGuidancePlaceholder}
              disabled={isSubmitting || isLoadingAction}
            />
            {showAutoEnhanceToggle && (
              <button
                type="button"
                className={`${styles.webSearchToggle} ${autoEnhancePrompt ? styles.webSearchToggleActive : ''}`}
                onClick={() => setAutoEnhancePrompt(!autoEnhancePrompt)}
                title={autoEnhanceTooltip}
                disabled={isSubmitting || isLoadingAction}
              >
                <TextFormatIcon fontSize="small" className={styles.webSearchIcon} />
                {autoEnhanceLabel}
              </button>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.confirmButton}
            onClick={handleSubmit}
            disabled={isGenerateDisabled}
          >
            {(isSubmitting || isLoadingAction) ? (
              <>
                <AutorenewIcon className={styles.loadingSpinner} fontSize="small" />
                Processing...
              </>
            ) : (
              'Start'
            )}
          </button>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            disabled={isSubmitting || isLoadingAction}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SparkActionModal;
